import { Button, Popover, Select, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationTypeEnum } from '../../enums/NotificationTypeEnum';
import { useNotifications } from '../../hooks/useNotifications';
import useOpenFile from '../../hooks/useOpenFile';
import { useTags } from '../../hooks/useTags';
import File from '../../interfaces/File';
import { FileService } from '../../services/fileService';
import { dateFromTicks, timeSince } from '../../services/utils';
import {
  getFileTitle,
  getFolderHex,
  removeShortcutExtension,
} from '../../utils/utils';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from './TruncatedTooltip';
import FileIcon from './FileIcon';

const fileService = new FileService();

interface FilterState {
  keyword: string;
  includeFolder: boolean;
  includeTags: boolean;
  offset: number;
  count: number;
  orderColumn: number;
  onBehalfOf?: number;
}

export default function GlobalSearchField() {
  const { createNotification } = useNotifications();
  const { t } = useTranslation();
  const { tags } = useTags();
  const { redirectFileToLocation } = useOpenFile();
  const [selectedFileUid, setSelectedFileUid] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [filesTotal, setFilesTotal] = useState<number>(0);

  const [filters, setFilters] = useState<FilterState>({
    keyword: '',
    includeFolder: true,
    includeTags: true,
    offset: 0,
    count: 10,
    orderColumn: 0,
  });

  // Reference to store debounce timeout
  const debounceTimeoutRef = useRef<number | null>(null);

  /**
   * Handles the search input change by updating the filters and resetting the offset.
   * The actual API call is debounced and handled in a useEffect.
   */
  const handleSearch = useCallback((newValue: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      keyword: newValue,
      offset: 0,
    }));
  }, []);

  /**
   * Fetches files from the API based on the provided filters.
   * If reset is true, it resets the files array; otherwise, it appends to it.
   */
  const getFiles = useCallback(
    async (newFilters: FilterState, reset = false) => {
      setIsLoading(true);

      await fileService
        .searchFiles({ ...newFilters })
        .then((response) => {
          const result = response?.myFiles || [];

          if (reset) {
            setFiles(result);
          } else {
            setFiles((prevFiles) => [...prevFiles, ...result]);
          }

          setFilesTotal(response.total);
        })
        .catch((error) => {
          createNotification(
            t('errors.genericError'),
            NotificationTypeEnum.DANGER
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [createNotification, t]
  );

  /**
   * Handles loading more files when the authenticatedUser scrolls to the bottom.
   * Increments the offset and fetches the next set of files.
   */
  const handleLoadMore = useCallback(() => {
    if (files.length < filesTotal && !isLoading) {
      const newFilters = {
        ...filters,
        offset: filters.offset + filters.count,
      };
      setFilters(newFilters);
      getFiles(newFilters, false).then(() => {});
    }
  }, [filters, getFiles, files.length, filesTotal, isLoading]);

  /**
   * Debounces the API call to get files whenever the search keyword changes.
   * Only calls getFiles if the keyword is not empty.
   */
  useEffect(() => {
    // Do nothing if keyword is empty or null
    if (!filters.keyword.trim()) {
      setFiles([]);
      setSelectedFileUid(null);
      return;
    }

    // Clear the previous debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set a new debounce timeout
    debounceTimeoutRef.current = window.setTimeout(() => {
      // Call getFiles with reset=true to reset the file list
      getFiles(filters, true).then(() => {});
    }, 300);

    // Cleanup function to clear timeout when component unmounts or keyword changes
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [filters.keyword, getFiles]);

  return (
    <>
      <Select
        className="lg:min-w-[500px]"
        showSearch
        labelInValue
        value={selectedFileUid}
        loading={isLoading}
        prefix={<Icon name="SearchIcon" className="h-4 w-4 text-gray-dark" />}
        placeholder={t('common.globalSearchPlaceholder')}
        notFoundContent={isLoading ? t('Loading...') : null}
        onSearch={handleSearch}
        onPopupScroll={(e) => {
          const target = e.target as HTMLDivElement;
          if (target.scrollTop + target.clientHeight === target.scrollHeight) {
            handleLoadMore();
          }
        }}
        onChange={(value, record) => {
          if (record && 'data' in record) {
            redirectFileToLocation(record.data as File);
          }
        }}
        filterOption={false}
        options={files.map((file) => ({
          value: file.uid,
          label: file.title,
          data: file,
        }))}
        optionRender={(option) => (
          <div className="flex w-full flex-row items-center justify-between">
            <div className="relative flex min-w-0 flex-1 flex-row items-center gap-2">
              <FileIcon
                isFolder={option.data.data.isFolder}
                fileExtension={removeShortcutExtension(
                  option.data.data.fileExtension
                )}
                isShortcut={option.data.data.isShortcut}
                style={{
                  color: getFolderHex(option.data.data),
                }}
                className="h-5 w-5"
              />
              {option.data.data.isFavourite && (
                <Icon
                  className="absolute -top-1 left-3 w-3"
                  name="StarShadowIcon"
                />
              )}

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-row items-center gap-1">
                  <span
                    title={removeShortcutExtension(
                      getFileTitle(option.data.data)
                    )}
                    className="overflow-hidden truncate whitespace-nowrap text-sm text-blue-dark"
                  >
                    {removeShortcutExtension(getFileTitle(option.data.data))}
                  </span>
                  <div className="flex items-center gap-2">
                    {option.data.data.tags?.map((id, index) => {
                      if (index < 1) {
                        const tag = tags.find((t) => t.id === id);
                        return (
                          tag?.fileVisible && (
                            <Tag
                              className="m-0 h-5 text-xs leading-[12px]"
                              style={{
                                color: tag?.color,
                                backgroundColor:
                                  getContrastBackgroundColorForText(tag?.color),
                              }}
                            >
                              <div className="flex flex-row items-center justify-center text-xs lg:justify-between">
                                <div />
                                <span
                                  className="max-w-28"
                                  style={{ color: tag?.color }}
                                >
                                  <TruncatedTooltip>
                                    {tag?.title}
                                  </TruncatedTooltip>
                                </span>
                                {tag.publicTag ? (
                                  <Icon
                                    name="BuildingIcon"
                                    className="h-3 w-3 text-gray-dark"
                                  />
                                ) : (
                                  <div className="w-1" />
                                )}
                              </div>
                            </Tag>
                          )
                        );
                      }
                    })}

                    {option.data.data.tags?.length > 1 && (
                      <Popover
                        zIndex={9999}
                        content={
                          <div className="flex w-full flex-col gap-2">
                            {option.data.data.tags
                              ?.slice(1, option.data.data.tags.length)
                              .map((id) => {
                                const tag = tags.find((t) => t.id === id);
                                return (
                                  tag?.fileVisible && (
                                    <Tag
                                      key={tag.id}
                                      style={{
                                        backgroundColor:
                                          getContrastBackgroundColorForText(
                                            tag.color
                                          ),
                                      }}
                                    >
                                      <div className="flex w-32 flex-row items-center justify-center text-xxs lg:justify-between">
                                        <div />
                                        <span
                                          className="max-w-28"
                                          style={{ color: tag.color }}
                                        >
                                          <TruncatedTooltip>
                                            {tag.title}
                                          </TruncatedTooltip>
                                        </span>
                                        {tag.publicTag ? (
                                          <Icon
                                            name="BuildingIcon"
                                            className="h-4 w-4 text-gray-dark"
                                          />
                                        ) : (
                                          <div className="w-1" />
                                        )}
                                      </div>
                                    </Tag>
                                  )
                                );
                              })}
                          </div>
                        }
                        trigger="hover"
                      >
                        <Button
                          size="small"
                          shape="circle"
                          className="!h-6 !w-6 !min-w-0 p-0"
                        >
                          <span className="text-xxs">
                            +{option.data.data.tags?.length - 1}
                          </span>
                        </Button>
                      </Popover>
                    )}
                  </div>
                </div>

                <span className="text-xs text-gray-dark">
                  {option.data.data.owner}
                </span>
              </div>
            </div>

            <span className="pl-2 text-sm text-gray-dark">
              {timeSince(dateFromTicks(option.data.data.createdAt))}
            </span>
            {!option.data.data.isFolder && (
              <Tooltip placement="right" title={t('common.openFileLocation')}>
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();

                    // Open the file location
                    redirectFileToLocation(option.data.data, true);
                  }}
                >
                  <Icon
                    name="ExternalLinkIcon"
                    className="h-4 w-4 text-gray-dark"
                  />
                </Button>
              </Tooltip>
            )}
          </div>
        )}
      />
    </>
  );
}
